export const ANALYTIC_TYPES = ['kpi', 'chart'];
export const ANALYTIC_AGGREGATIONS = ['row_count', 'value_count', 'distinct_count', 'sum', 'average', 'min', 'max'];
export const ANALYTIC_CHART_TYPES = ['bar', 'line', 'pie'];
export const ANALYTIC_DATE_GRAINS = ['none', 'day', 'week', 'month', 'quarter', 'year'];
export const ANALYTIC_COLOR_SCHEMES = ['workspace', 'sheet', 'custom', 'default', 'warm', 'cool', 'vibrant'];
export const ANALYTIC_LAYOUT_SIZES = ['small', 'medium', 'wide'];
export const ANALYTIC_DATE_PRESETS = ['all', 'last_7_days', 'last_30_days', 'this_month', 'this_quarter', 'this_year', 'custom'];
export const ANALYTIC_FILTER_OPERATORS = ['equals', 'not_equals', 'contains', 'not_contains', 'greater_than', 'greater_or_equal', 'less_than', 'less_or_equal', 'between', 'is_empty', 'is_not_empty'];

const NUMERIC_TYPES = new Set(['number', 'rating', 'currency', 'percent']);
const DATE_TYPES = new Set(['date', 'datetime']);
const CATEGORY_TYPES = new Set(['select', 'buttons', 'yesno', 'rog', 'multiselect', 'list', 'checkbox']);
const enumValue = (value, allowed, fallback) => allowed.includes(`${value || ''}`.trim()) ? `${value}`.trim() : fallback;
export const createAnalyticId = () => `analytic_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
export const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export const createDefaultAnalytic = (type = 'kpi') => ({
  id: createAnalyticId(),
  type: type === 'chart' ? 'chart' : 'kpi',
  title: '',
  description: '',
  metric: { aggregation: 'row_count', headerId: '' },
  metrics: type === 'chart' ? [{ id: createAnalyticId(), aggregation: 'row_count', headerId: '' }] : [],
  grouping: { headerId: '', seriesHeaderId: '', dateGrain: 'none', limit: 10, sort: 'value_desc' },
  filters: [],
  dateRange: { headerId: '', preset: 'all', start: '', end: '', timezone: getBrowserTimeZone() },
  comparison: { enabled: false, mode: 'previous_period' },
  target: { enabled: false, value: 0, direction: 'at_least' },
  visual: { chartType: 'bar', colorScheme: 'workspace', customColor: '#008080', size: type === 'chart' ? 'wide' : 'small' },
  placement: { showInSheet: true, showInWorkspace: false, headline: false, order: 0 }
});

export const sanitizeAnalytics = (analytics = [], headers = []) => {
  const headerMap = new Map((Array.isArray(headers) ? headers : []).filter((header) => header?.id).map((header) => [header.id, header]));
  const usedIds = new Set();
  return (Array.isArray(analytics) ? analytics : []).slice(0, 30).map((source, index) => {
    const type = enumValue(source?.type, ANALYTIC_TYPES, 'kpi');
    let id = `${source?.id || ''}`.trim() || createAnalyticId();
    if (usedIds.has(id)) id = createAnalyticId();
    usedIds.add(id);
    const sourceMetrics = type === 'chart' && Array.isArray(source?.metrics) && source.metrics.length
      ? source.metrics
      : [source?.metric || {}];
    const metrics = sourceMetrics.slice(0, 10).map((metric, metricIndex) => {
      const aggregation = enumValue(metric?.aggregation, ANALYTIC_AGGREGATIONS, 'row_count');
      const metricHeader = headerMap.get(`${metric?.headerId || ''}`.trim());
      return {
        id: `${metric?.id || `${id}_metric_${metricIndex}`}`.trim(),
        aggregation,
        headerId: aggregation === 'row_count' ? '' : (metricHeader?.id || '')
      };
    }).filter((metric) => metric.aggregation === 'row_count' || metric.headerId);
    const primaryMetric = metrics[0] || { id: `${id}_metric_0`, aggregation: 'row_count', headerId: '' };
    const groupingHeader = headerMap.get(`${source?.grouping?.headerId || ''}`.trim());
    const seriesHeader = headerMap.get(`${source?.grouping?.seriesHeaderId || ''}`.trim());
    const dateHeader = headerMap.get(`${source?.dateRange?.headerId || ''}`.trim());
    const chartType = enumValue(source?.visual?.chartType, ANALYTIC_CHART_TYPES, 'bar');
    const showInWorkspace = source?.placement?.showInWorkspace === true;
    return {
      id,
      type,
      title: `${source?.title || ''}`.trim().slice(0, 120),
      description: `${source?.description || ''}`.trim().slice(0, 240),
      metric: { aggregation: primaryMetric.aggregation, headerId: primaryMetric.headerId },
      metrics: type === 'chart' ? metrics : [],
      grouping: {
        headerId: type === 'chart' ? (groupingHeader?.id || '') : '',
        seriesHeaderId: type === 'chart' ? (seriesHeader?.id || '') : '',
        dateGrain: groupingHeader && DATE_TYPES.has(groupingHeader.type)
          ? enumValue(source?.grouping?.dateGrain, ANALYTIC_DATE_GRAINS, 'month')
          : 'none',
        limit: Math.max(1, Math.min(50, Number(source?.grouping?.limit) || 10)),
        sort: ['label_asc', 'label_desc', 'value_asc', 'value_desc'].includes(source?.grouping?.sort) ? source.grouping.sort : 'value_desc'
      },
      filters: (Array.isArray(source?.filters) ? source.filters : []).slice(0, 10).map((filter) => ({
        id: `${filter?.id || createAnalyticId()}`,
        headerId: headerMap.has(`${filter?.headerId || ''}`.trim()) ? `${filter.headerId}`.trim() : '',
        operator: enumValue(filter?.operator, ANALYTIC_FILTER_OPERATORS, 'equals'),
        value: filter?.value ?? '',
        secondValue: filter?.secondValue ?? ''
      })).filter((filter) => filter.headerId),
      dateRange: {
        headerId: dateHeader && DATE_TYPES.has(dateHeader.type) ? dateHeader.id : '',
        preset: enumValue(source?.dateRange?.preset, ANALYTIC_DATE_PRESETS, 'all'),
        start: `${source?.dateRange?.start || ''}`.trim(),
        end: `${source?.dateRange?.end || ''}`.trim(),
        timezone: `${source?.dateRange?.timezone || getBrowserTimeZone()}`.trim().slice(0, 80) || 'UTC'
      },
      comparison: {
        enabled: source?.comparison?.enabled === true,
        mode: source?.comparison?.mode === 'previous_year' ? 'previous_year' : 'previous_period'
      },
      target: {
        enabled: source?.target?.enabled === true,
        value: Number.isFinite(Number(source?.target?.value)) ? Number(source.target.value) : 0,
        direction: source?.target?.direction === 'at_most' ? 'at_most' : 'at_least'
      },
      visual: {
        chartType,
        colorScheme: enumValue(source?.visual?.colorScheme, ANALYTIC_COLOR_SCHEMES, 'workspace'),
        customColor: /^#[0-9a-f]{6}$/i.test(`${source?.visual?.customColor || ''}`) ? source.visual.customColor : '#008080',
        size: enumValue(source?.visual?.size, ANALYTIC_LAYOUT_SIZES, type === 'chart' ? 'wide' : 'small')
      },
      placement: {
        showInSheet: source?.placement?.showInSheet !== false,
        showInWorkspace,
        headline: type === 'kpi' && showInWorkspace && source?.placement?.headline === true,
        order: Number.isFinite(Number(source?.placement?.order)) ? Number(source.placement.order) : index
      }
    };
  }).filter((analytic) => analytic.title && (analytic.metric.aggregation === 'row_count' || analytic.metric.headerId) && (analytic.type !== 'chart' || (analytic.grouping.headerId && analytic.metrics.length)));
};

export const suggestAnalytics = (headers = []) => {
  const safeHeaders = (Array.isArray(headers) ? headers : []).filter((header) => header?.id && header?.label);
  const numeric = safeHeaders.filter((header) => NUMERIC_TYPES.has(header.type));
  const category = safeHeaders.find((header) => CATEGORY_TYPES.has(header.type) && /status|stage|category|priority|type/i.test(header.label))
    || safeHeaders.find((header) => CATEGORY_TYPES.has(header.type));
  const date = safeHeaders.find((header) => DATE_TYPES.has(header.type));
  const suggestions = [];
  const total = createDefaultAnalytic('kpi');
  total.title = 'Total records';
  total.placement.showInWorkspace = true;
  total.placement.headline = true;
  suggestions.push(total);
  numeric.slice(0, 2).forEach((header) => {
    const analytic = createDefaultAnalytic('kpi');
    analytic.title = `${header.type === 'percent' ? 'Average' : 'Total'} ${header.label}`;
    analytic.metric = { aggregation: header.type === 'percent' ? 'average' : 'sum', headerId: header.id };
    analytic.placement.showInWorkspace = true;
    analytic.placement.headline = true;
    suggestions.push(analytic);
  });
  if (category) {
    const analytic = createDefaultAnalytic('chart');
    analytic.title = `${category.label} breakdown`;
    analytic.metric = { aggregation: 'row_count', headerId: '' };
    analytic.grouping.headerId = category.id;
    analytic.visual.chartType = 'pie';
    analytic.placement.showInWorkspace = true;
    suggestions.push(analytic);
  }
  if (date) {
    const analytic = createDefaultAnalytic('chart');
    analytic.title = `${numeric[0]?.label || 'Records'} over time`;
    analytic.metric = numeric[0] ? { aggregation: 'sum', headerId: numeric[0].id } : { aggregation: 'row_count', headerId: '' };
    analytic.metrics = [{ id: `${analytic.id}_metric_0`, ...analytic.metric }];
    analytic.grouping = { ...analytic.grouping, headerId: date.id, dateGrain: 'month', sort: 'label_asc' };
    analytic.visual.chartType = 'line';
    analytic.placement.showInWorkspace = true;
    suggestions.push(analytic);
  }
  return suggestions.map((analytic, index) => ({ ...analytic, placement: { ...analytic.placement, order: index } }));
};

export const analyticsHeaderTypeSets = { NUMERIC_TYPES, DATE_TYPES, CATEGORY_TYPES };
